import React from 'react';
import { Route,
  RouterProvider,
  createBrowserRouter,
  NonIndexRouteObject,
  IndexRouteObject,
  Navigate } from 'react-router-dom';
import { AnyObject } from '@triare/auth-redux';
import { paramsToString, routes as menuRoutes } from '../components/Layout/Menu';
import PrivateRoute from './PrivateRoute';
import { User } from '../store/auth';
import { Role } from '../enums/user';
import SignIn from '../pages/Auth/SignIn';
import ForgotPassword from '../pages/Auth/ForgotPassword';
import ResetPassword from '../pages/Auth/ResetPassword';
import ConfirmEmail from '../pages/Auth/ConfirmEmail';
import NotFound from '../pages/NotFound';

export function generateDefaultUrl(user?: User | null) {
  return `${paramsToString(user, user && user?.role === Role.ADMIN ? undefined : getRouteParams('/', user))}`;
}

export interface RouteElements {
  [role: string]: JSX.Element | null;
}

interface RouteObjectExtension {
  elements?: RouteElements;
  params?: ((user: User) => string) | AnyObject;
}

interface MyIndexRouteObject extends IndexRouteObject, RouteObjectExtension {}

interface MyNonIndexRouteObject extends NonIndexRouteObject, RouteObjectExtension {}

export type RouteObject = MyIndexRouteObject | MyNonIndexRouteObject;
export type RouteRoles = (string | ((user: User) => boolean))[];

export interface Route {
  route?: RouteObject;
  roles?: RouteRoles;
  name?: string | ((user: User) => string);
  icon?: React.ReactNode | ((user: User) => React.ReactNode);
  parent?: Route;
  hidden?: boolean;
  onClick?: () => void;
  children?: Route[];
  outlet?: true;
  roleNeeded?: 'user' | 'manager' | 'admin';
  privateRoute?: boolean;
  disabled?: string | ((user: User) => boolean);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export function addChildrenRoutes(routes: Route[]): Route[] {
  const flatRoutes: Route[] = [];

  const processRoutes = (routeItems: Route[], parentPath: string) => {
    for (const item of routeItems) {
      // Make a shallow copy of the item to avoid mutating the original
      const newItem = { ...item };

      if (newItem.route) {
        // Create a new route object to avoid mutation
        newItem.route = { ...newItem.route };

        const currentPath = [parentPath, newItem.route.path]
          .filter(Boolean) // Remove any empty/null parts
          .join('/');

        newItem.route.path = currentPath;

        // If the route has a renderable element, add it to our list.
        // This automatically discards parent container routes that have no element.
        if (newItem.route.element) {
          // Wrap in PrivateRoute if needed
          if (newItem.privateRoute) {
            newItem.route.element = (
              <PrivateRoute
                element={newItem.route.element as JSX.Element}
                elements={newItem.route.elements}
                item={newItem}
              />
            );
          }
          flatRoutes.push(newItem);
        }

        // If there are children, recurse with the newly formed path
        if (item.children) {
          processRoutes(item.children, currentPath);
        }
      } else if (item.children) {
        // This handles cases where a grouping item has no `route` property itself
        processRoutes(item.children, parentPath);
      }
    }
  };

  processRoutes(routes, '');

  // Filter out any routes that don't have a route object after processing
  return flatRoutes.filter(({ route }) => route);
}

export const routes: Route[] = addChildrenRoutes([
  {
    name: 'Sign In',
    route: {
      path: 'sign-in',
      element: <SignIn />,
    },
  },
  {
    name: 'Forgot password',
    route: {
      path: 'forgot-password',
      element: <ForgotPassword />,
    },
  },
  {
    name: 'Reset password',
    route: {
      path: 'reset-password/:secretKey',
      element: <ResetPassword />,
    },
  },
  {
    name: 'Confirm email',
    route: {
      path: 'complete-registration/:mode/:secretKey',
      element: <ConfirmEmail />,
    },
  },
  {
    privateRoute: true,
    name: 'Home',
    route: {
      path: '/',
      element: <Navigate to="/categories" replace />,
    },
    children: menuRoutes,
  },
]);

const router = createBrowserRouter([
  ...routes.map(({ route }) => route as RouteObject),
  {
    path: '*',
    element: <NotFound />,
  },
]);

export function getRouteParams(path: string, user?: User | null): AnyObject {
  let params: ((user: User) => string) | AnyObject = {};

  routes.find(({ route }) => {
    if (route && route.path && route.path === path) {
      if (route.params) {
        params = route.params;
      }

      return true;
    }

    return false;
  });

  if (typeof params === 'function') {
    if (user) {
      return params(user);
    }

    return {};
  }

  return params;
}

export default function Routes(): JSX.Element {
  return <RouterProvider router={router} />;
}
